import React, { useState, useEffect } from 'react';
import { Video, Clock, AlertTriangle } from 'lucide-react';
import VideoForm from '../components/youtube-reel/VideoForm';
import SegmentEditor from '../components/youtube-reel/SegmentEditor';
import SegmentList from '../components/youtube-reel/SegmentList';
import YouTubeEmbed from '../components/youtube-reel/YouTubeEmbed';
import { getVideoInfo, processVideo } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { recordUserActivity } from '../services/firebase';

const YoutubeReel = () => {
  // State hooks
  const [videoUrl, setVideoUrl] = useState('');
  const [videoInfo, setVideoInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timeSegments, setTimeSegments] = useState([]);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [isProcessing, setIsProcessing] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const { currentUser } = useAuth();
  
  // Reset error when component unmounts or when dependencies change
  useEffect(() => {
    return () => setError(null);
  }, [videoUrl]);
  
  // Handle video URL submission
  const handleVideoSubmit = async (url) => {
    setIsLoading(true);
    setError(null);
    setVideoUrl(url);
    setDownloadUrl('');
    setTimeSegments([]);
    setVideoLoaded(false);
    
    try {
      const info = await getVideoInfo(url);
      setVideoInfo(info);
      setVideoLoaded(true);
    } catch (error) {
      console.error('Error fetching video info:', error);
      setError('Failed to load video. Please check the URL and try again.');
      setVideoInfo(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle video duration change from YouTube embed
  const handleDurationChange = (duration) => {
    if (duration && duration > 0) {
      setVideoInfo(prev => ({
        ...prev,
        duration: duration
      }));
      setVideoLoaded(true);
    }
  };
  
  // Add a new time segment
  const handleAddSegment = (newSegment) => {
    setTimeSegments([...timeSegments, newSegment]);
  };
  
  // Remove a time segment
  const handleRemoveSegment = (index) => {
    const updatedSegments = timeSegments.filter((_, i) => i !== index);
    setTimeSegments(updatedSegments);
  };
  
  // Update a time segment
  const handleUpdateSegment = (index, field, value) => {
    const updatedSegments = [...timeSegments];
    updatedSegments[index] = {
      ...updatedSegments[index],
      [field]: value
    };
    setTimeSegments(updatedSegments);
  };
  
  // Process the video
  const handleProcessVideo = async (settings) => {
    if (!videoInfo || timeSegments.length === 0) return;
    
    setIsProcessing(true);
    setError(null);
    setDownloadUrl('');
    
    try {
      // Add progress callback to the processVideo function
      const result = await processVideo({
        url: videoUrl,
        videoId: videoInfo.videoId,
        segments: timeSegments,
        aspectRatio: settings.aspectRatio,
        transitionType: settings.transitionType,
        quality: settings.quality
      }, (progress, stage, error) => {
        // This callback will be called with progress updates
        console.log(`Processing progress: ${progress}%, stage: ${stage}`);
        
        // Pass progress to the SegmentList component
        if (settings.onProgressUpdate) {
          settings.onProgressUpdate(progress, stage, error);
        }
        
        // Handle errors immediately if they're severe
        if (progress === -1 && error) {
          throw new Error(error);
        }
      });
      
      if (result.success) {
        setDownloadUrl(result.downloadUrl);
        
        // Record user activity in Firestore
        if (currentUser) {
          await recordUserActivity(
            currentUser.uid,
            'youtube-reel',
            'YouTube Reel Creator'
          );
        }
      } else {
        throw new Error(result.error || 'Failed to process video');
      }
    } catch (error) {
      console.error('Error processing video:', error);
      
      // Provide a more helpful error message based on the error type
      if (error.message.includes('timed out')) {
        setError('Video processing timed out. Please try a shorter video or fewer segments.');
      } else if (error.message.includes('download')) {
        setError('Failed to download the YouTube video. This could be due to YouTube restrictions or the video may be private or unavailable.');
      } else if (error.message.includes('connection')) {
        setError('Lost connection to the server during processing. Please check your internet connection and try again.');
      } else {
        setError(`Failed to process video: ${error.message}. Please try again with different settings or a different video.`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-2 flex items-center justify-center">
          <Video className="mr-2 text-indigo-600" size={32} />
          YouTube Reel Creator
        </h1>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Transform YouTube videos into perfect social media reels. Extract the best moments 
          and convert them to any aspect ratio for Instagram, TikTok, or any platform.
        </p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Video Input and Preview */}
        <div className="lg:col-span-2 space-y-6">
          <VideoForm 
            onVideoSubmit={handleVideoSubmit}
            isLoading={isLoading}
          />
          
          {videoInfo && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold mb-3 flex items-center">
                Video Details
                {videoInfo.duration > 0 && (
                  <span className="ml-auto text-sm flex items-center text-gray-600">
                    <Clock size={16} className="mr-1" />
                    {Math.floor(videoInfo.duration / 60)}:{String(Math.floor(videoInfo.duration % 60)).padStart(2, '0')}
                  </span>
                )}
              </h2>
              
              <div className="bg-black rounded-md overflow-hidden mb-4">
                {videoInfo.videoId ? (
                  <YouTubeEmbed 
                    videoId={videoInfo.videoId}
                    onDurationChange={handleDurationChange}
                  />
                ) : (
                  <div className="aspect-video flex items-center justify-center text-white">
                    <p>Loading video...</p>
                  </div>
                )}
              </div>
              
              <div className="text-sm mt-3">
                <h3 className="font-medium">{videoInfo.title || 'YouTube Video'}</h3>
                <p className="text-gray-600 mt-1">
                  Select the segments you want to include in your reel using the editor below.
                </p>
              </div>
            </div>
          )}
          
          {/* Always show the segment editor once a video URL has been entered */}
          {videoInfo && (
            <SegmentEditor 
              timeSegments={timeSegments}
              onAddSegment={handleAddSegment}
              onRemoveSegment={handleRemoveSegment}
              onUpdateSegment={handleUpdateSegment}
              videoDuration={videoInfo.duration || 0}
              videoLoaded={videoLoaded}
            />
          )}
        </div>
        
        {/* Right Column - Configuration */}
        <div>
          <div className="sticky top-4">
            <SegmentList
              aspectRatio={aspectRatio}
              setAspectRatio={setAspectRatio}
              timeSegments={timeSegments}
              onProcessVideo={(settings) => {
                // Add progress update callback to settings
                handleProcessVideo({
                  ...settings,
                  onProgressUpdate: (progress, stage, error) => {
                    // This will be called with progress updates from the API
                    console.log(`Updating UI with progress: ${progress}%, stage: ${stage}`);
                    
                    // Handle error messages if they come through the progress updates
                    if (error) {
                      setError(error);
                    }
                  }
                });
              }}
              isProcessing={isProcessing}
              downloadUrl={downloadUrl}
            />
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium mb-2">How It Works</h3>
              <ol className="text-sm text-gray-600 space-y-2 list-decimal pl-5">
                <li>Enter a YouTube video URL</li>
                <li>Select the time segments you want to extract</li>
                <li>Choose your desired aspect ratio and settings</li>
                <li>Click "Create Reel" to generate your video</li>
                <li>Download your new reel</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubeReel;
